import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Calendar, ChevronLeft, ChevronRight, Plus, Edit, Trash2, Users,
  ArrowLeft, Save, X, Loader2, BookOpen, Music, UserCheck, Repeat, CalendarPlus
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getErrorMessage } from '../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role colors for display
const ROLE_COLORS = {
  limpieza: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
  ninos: 'bg-pink-500/20 text-pink-300 border-pink-500/50',
  jovenes: 'bg-purple-500/20 text-purple-300 border-purple-500/50',
  audio: 'bg-green-500/20 text-green-300 border-green-500/50',
  alabanza: 'bg-amber-500/20 text-amber-300 border-amber-500/50',
  servicio: 'bg-red-500/20 text-red-300 border-red-500/50',
  multimedia: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/50',
};

const ServiceCalendar = () => {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [members, setMembers] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [recurringEvents, setRecurringEvents] = useState([]);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    assignments: {},
    theme: '',
    scripture: '',
    notes: ''
  });
  
  // Recurring event form state
  const [recurringFormData, setRecurringFormData] = useState({
    title: '',
    startDate: '',
    pattern: 'weekly',
    daysOfWeek: [],
    dayOfMonth: 1,
    endAfter: 12,
    endDate: '',
    useEndDate: false,
    defaultAssignments: {},
    theme: '',
    notes: ''
  });

  // Days of week - will be translated via t() function
  const getDaysOfWeek = () => [
    { value: 'sunday', label: t('sunday') },
    { value: 'monday', label: t('monday') },
    { value: 'tuesday', label: t('tuesday') },
    { value: 'wednesday', label: t('wednesday') },
    { value: 'thursday', label: t('thursday') },
    { value: 'friday', label: t('friday') },
    { value: 'saturday', label: t('saturday') },
  ];

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/communities/${communityId}/roles`);
      const allRoles = [...response.data.default_roles, ...response.data.custom_roles];
      setRoles(allRoles);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  }, [communityId]);

  const fetchSchedules = useCallback(async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await axios.get(`${API}/calendar/communities/${communityId}/schedules`, {
        params: { year, month }
      });
      setSchedules(response.data.schedules);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    } finally {
      setLoading(false);
    }
  }, [communityId, currentMonth]);

  const fetchMembers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/communities/${communityId}/assignable-members`);
      setMembers(response.data.members);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  }, [communityId]);

  const fetchRecurringEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar/communities/${communityId}/recurring-events`);
      setRecurringEvents(response.data.recurring_events || []);
    } catch (error) {
      console.error('Failed to fetch recurring events:', error);
    }
  }, [communityId]);

  useEffect(() => {
    fetchRoles();
    fetchMembers();
    fetchRecurringEvents();
  }, [fetchRoles, fetchMembers, fetchRecurringEvents]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Create recurring event
  const handleCreateRecurringEvent = async () => {
    if (!recurringFormData.title || !recurringFormData.startDate) {
      toast.error('Title and start date are required');
      return;
    }

    try {
      const payload = {
        title: recurringFormData.title,
        start_date: recurringFormData.startDate,
        pattern: recurringFormData.pattern,
        days_of_week: recurringFormData.pattern === 'weekly' ? recurringFormData.daysOfWeek : [],
        day_of_month: recurringFormData.pattern === 'monthly' ? recurringFormData.dayOfMonth : null,
        end_date: recurringFormData.useEndDate ? recurringFormData.endDate : null,
        end_after_occurrences: !recurringFormData.useEndDate ? recurringFormData.endAfter : null,
        default_assignments: recurringFormData.defaultAssignments,
        theme: recurringFormData.theme,
        notes: recurringFormData.notes
      };

      const response = await axios.post(
        `${API}/calendar/communities/${communityId}/recurring-events`,
        payload
      );

      toast.success(`Created ${response.data.occurrences_created} service dates`);
      setRecurringDialogOpen(false);
      fetchSchedules();
      fetchRecurringEvents();
      
      // Reset form
      setRecurringFormData({
        title: '',
        startDate: '',
        pattern: 'weekly',
        daysOfWeek: [],
        dayOfMonth: 1,
        endAfter: 12,
        endDate: '',
        useEndDate: false,
        defaultAssignments: {},
        theme: '',
        notes: ''
      });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create recurring event'));
    }
  };

  // Delete recurring event
  const handleDeleteRecurringEvent = async (templateId, deleteSchedules = false) => {
    if (!confirm(deleteSchedules 
      ? 'Delete this recurring event and all future schedules?' 
      : 'Delete this recurring event template? Existing schedules will remain.')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/calendar/communities/${communityId}/recurring-events/${templateId}`,
        { params: { delete_schedules: deleteSchedules } }
      );
      toast.success('Recurring event deleted');
      fetchRecurringEvents();
      if (deleteSchedules) fetchSchedules();
    } catch (error) {
      toast.error('Failed to delete recurring event');
    }
  };

  // Get Sundays in the current month
  const getSundaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const sundays = [];
    const date = new Date(year, month, 1);
    
    while (date.getMonth() === month) {
      if (date.getDay() === 0) { // Sunday
        sundays.push(new Date(date));
      }
      date.setDate(date.getDate() + 1);
    }
    
    return sundays;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
  };

  const getScheduleForDate = (date) => {
    const dateStr = formatDate(date);
    return schedules.find(s => s.service_date === dateStr);
  };

  const openEditDialog = (date, existingSchedule = null) => {
    setSelectedDate(date);
    setSelectedSchedule(existingSchedule);
    
    if (existingSchedule) {
      setFormData({
        title: existingSchedule.title || '',
        assignments: existingSchedule.assignments || {},
        theme: existingSchedule.theme || '',
        scripture: existingSchedule.scripture || '',
        notes: existingSchedule.notes || ''
      });
    } else {
      setFormData({
        title: 'Servicio Dominical',
        assignments: {},
        theme: '',
        scripture: '',
        notes: ''
      });
    }
    
    setEditDialogOpen(true);
  };

  const handleSaveSchedule = async () => {
    try {
      const dateStr = formatDate(selectedDate);
      
      if (selectedSchedule) {
        // Update existing
        await axios.put(`${API}/calendar/communities/${communityId}/schedules/${selectedSchedule.schedule_id}`, formData);
        toast.success('Schedule updated');
      } else {
        // Create new
        await axios.post(`${API}/calendar/communities/${communityId}/schedules`, {
          service_date: dateStr,
          ...formData
        });
        toast.success('Schedule created');
      }
      
      setEditDialogOpen(false);
      fetchSchedules();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save schedule'));
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      await axios.delete(`${API}/calendar/communities/${communityId}/schedules/${scheduleId}`);
      toast.success('Schedule deleted');
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const updateAssignment = (roleKey, value) => {
    setFormData(prev => ({
      ...prev,
      assignments: {
        ...prev.assignments,
        [roleKey]: value.split(',').map(v => v.trim()).filter(v => v)
      }
    }));
  };

  const sundays = getSundaysInMonth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-xl">{t('service_calendar')}</h1>
                <p className="text-sm text-slate-400">{t('schedules')}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Action Buttons */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            onClick={() => setRecurringDialogOpen(true)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Repeat className="w-4 h-4 mr-2" />
            {t('create_recurring')}
          </Button>
          
          {recurringEvents.length > 0 && (
            <div className="text-sm text-slate-400">
              {recurringEvents.length} {t('recurring_events')}
            </div>
          )}
        </div>

        {/* Recurring Events Cards */}
        {recurringEvents.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Repeat className="w-5 h-5 text-purple-400" />
              Active Recurring Events
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recurringEvents.map(event => (
                <Card key={event.template_id} className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{event.title}</CardTitle>
                        <CardDescription className="capitalize">
                          {event.recurrence?.pattern} • Started {event.start_date}
                        </CardDescription>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleDeleteRecurringEvent(event.template_id, true)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {event.recurrence?.days_of_week?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {event.recurrence.days_of_week.map(day => (
                          <Badge key={day} variant="secondary" className="text-xs capitalize">
                            {day}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {event.theme && (
                      <p className="text-xs text-slate-400 mt-2">{event.theme}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="outline"
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <h2 className="text-2xl font-bold">
            {currentMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
          </h2>
          
          <Button
            variant="outline"
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Calendar Table */}
        <Card className="bg-slate-900 border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-800">
                  <th className="p-3 text-left border-b border-slate-700 min-w-[120px]">Fecha</th>
                  {roles.map(role => (
                    <th key={role.key} className="p-3 text-center border-b border-slate-700 min-w-[140px]">
                      <span className="block font-semibold">{role.name}</span>
                      <span className="text-xs text-slate-400">{role.name_en}</span>
                    </th>
                  ))}
                  <th className="p-3 text-center border-b border-slate-700 w-[100px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sundays.map((sunday, idx) => {
                  const schedule = getScheduleForDate(sunday);
                  const dateStr = formatDate(sunday);
                  
                  return (
                    <motion.tr
                      key={dateStr}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="border-b border-slate-800 hover:bg-slate-800/50"
                    >
                      <td className="p-3">
                        <div className="font-semibold">{formatDisplayDate(dateStr)}</div>
                        <div className="text-xs text-slate-400">
                          {sunday.toLocaleDateString('es-ES', { weekday: 'long' })}
                        </div>
                        {schedule?.title && (
                          <Badge variant="outline" className="mt-1 text-xs">
                            {schedule.title}
                          </Badge>
                        )}
                      </td>
                      
                      {roles.map(role => {
                        const assigned = schedule?.assignments?.[role.key] || [];
                        return (
                          <td key={role.key} className="p-2 text-center">
                            {assigned.length > 0 ? (
                              <div className="space-y-1">
                                {assigned.map((name, i) => (
                                  <Badge
                                    key={i}
                                    variant="outline"
                                    className={`text-xs ${ROLE_COLORS[role.key] || 'bg-slate-500/20'}`}
                                  >
                                    {name}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-slate-600">-</span>
                            )}
                          </td>
                        );
                      })}
                      
                      <td className="p-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(sunday, schedule)}
                          >
                            {schedule ? <Edit className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                          </Button>
                          {schedule && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400"
                              onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Theme/Scripture Section */}
        {schedules.some(s => s.theme || s.scripture) && (
          <Card className="bg-slate-900 border-slate-800 mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Monthly Themes & Scripture
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {schedules.filter(s => s.theme || s.scripture).map(schedule => (
                  <div key={schedule.schedule_id} className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="font-semibold text-amber-400 mb-2">
                      {formatDisplayDate(schedule.service_date)}
                    </div>
                    {schedule.theme && (
                      <p className="text-sm mb-2">
                        <span className="text-slate-400">{t('theme')}:</span> {schedule.theme}
                      </p>
                    )}
                    {schedule.scripture && (
                      <p className="text-sm italic text-slate-300">{schedule.scripture}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Edit Schedule Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" />
              {selectedSchedule ? t('edit_schedule') : t('add_schedule')}
            </DialogTitle>
            <DialogDescription>
              {selectedDate && formatDisplayDate(formatDate(selectedDate))}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label>{t('schedule_details')}</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={t('schedule_details')}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            {/* Theme & Scripture */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('theme')}</Label>
                <Input
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  placeholder={t('theme')}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('scripture')}</Label>
                <Input
                  value={formData.scripture}
                  onChange={(e) => setFormData({ ...formData, scripture: e.target.value })}
                  placeholder={t('scripture')}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            {/* Role Assignments */}
            <div className="space-y-4">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                {t('assignments')}
              </Label>
              <p className="text-xs text-slate-400">
                {t('assign_member')}
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {roles.map(role => (
                  <div key={role.key} className="space-y-1">
                    <Label className="text-sm">
                      {role.name}
                    </Label>
                    <Input
                      value={formData.assignments[role.key]?.join(', ') || ''}
                      onChange={(e) => updateAssignment(role.key, e.target.value)}
                      placeholder={t('select_member')}
                      className="bg-slate-800 border-slate-700 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Member Select */}
            {members.length > 0 && (
              <div className="space-y-2">
                <Label>{t('members')}</Label>
                <ScrollArea className="h-32 rounded border border-slate-700 p-2">
                  <div className="flex flex-wrap gap-2">
                    {members.map(member => (
                      <Badge
                        key={member.user_id}
                        variant="outline"
                        className="cursor-pointer hover:bg-slate-700"
                        onClick={() => {
                          navigator.clipboard.writeText(member.name);
                          toast.success(`${t('copied')}`);
                        }}
                      >
                        <UserCheck className="w-3 h-3 mr-1" />
                        {member.name}
                      </Badge>
                    ))}
                  </div>
                </ScrollArea>
                <p className="text-xs text-slate-500">{t('copy')}</p>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('notes')}
                className="bg-slate-800 border-slate-700"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" />
              {t('cancel')}
            </Button>
            <Button onClick={handleSaveSchedule} className="bg-amber-500 hover:bg-amber-600 text-black">
              <Save className="w-4 h-4 mr-2" />
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurring Event Dialog */}
      <Dialog open={recurringDialogOpen} onOpenChange={setRecurringDialogOpen}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-purple-400" />
              {t('create_recurring')}
            </DialogTitle>
            <DialogDescription>
              {t('recurring_pattern')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('schedule_details')} *</Label>
                <Input
                  value={recurringFormData.title}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, title: e.target.value })}
                  placeholder={t('schedule_details')}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('start_date')} *</Label>
                <Input
                  type="date"
                  value={recurringFormData.startDate}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, startDate: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>

            {/* Recurrence Pattern */}
            <div className="space-y-2">
              <Label>{t('recurring_pattern')}</Label>
              <Select
                value={recurringFormData.pattern}
                onValueChange={(value) => setRecurringFormData({ ...recurringFormData, pattern: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">{t('weekly')}</SelectItem>
                  <SelectItem value="biweekly">{t('weekly')} x2</SelectItem>
                  <SelectItem value="monthly">{t('monthly')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Days of Week (for weekly) */}
            {recurringFormData.pattern === 'weekly' && (
              <div className="space-y-2">
                <Label>{t('days_of_week')}</Label>
                <div className="flex flex-wrap gap-2">
                  {getDaysOfWeek().map(day => (
                    <label key={day.value} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={recurringFormData.daysOfWeek.includes(day.value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setRecurringFormData({
                              ...recurringFormData,
                              daysOfWeek: [...recurringFormData.daysOfWeek, day.value]
                            });
                          } else {
                            setRecurringFormData({
                              ...recurringFormData,
                              daysOfWeek: recurringFormData.daysOfWeek.filter(d => d !== day.value)
                            });
                          }
                        }}
                      />
                      <span className="text-sm">{day.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Day of Month (for monthly) */}
            {recurringFormData.pattern === 'monthly' && (
              <div className="space-y-2">
                <Label>{t('day_of_month')}</Label>
                <Input
                  type="number"
                  min="1"
                  max="31"
                  value={recurringFormData.dayOfMonth}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, dayOfMonth: parseInt(e.target.value) })}
                  className="bg-slate-800 border-slate-700 w-24"
                />
              </div>
            )}

            {/* End Condition */}
            <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg">
              <Label>{t('end_after')}</Label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!recurringFormData.useEndDate}
                    onChange={() => setRecurringFormData({ ...recurringFormData, useEndDate: false })}
                    className="text-purple-500"
                  />
                  <Input
                    type="number"
                    min="1"
                    max="52"
                    value={recurringFormData.endAfter}
                    onChange={(e) => setRecurringFormData({ ...recurringFormData, endAfter: parseInt(e.target.value) })}
                    className="bg-slate-700 border-slate-600 w-20 h-8"
                    disabled={recurringFormData.useEndDate}
                  />
                  <span className="text-sm">{t('occurrences')}</span>
                </label>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={recurringFormData.useEndDate}
                    onChange={() => setRecurringFormData({ ...recurringFormData, useEndDate: true })}
                    className="text-purple-500"
                  />
                  <span className="text-sm">{t('end_date')}</span>
                  <Input
                    type="date"
                    value={recurringFormData.endDate}
                    onChange={(e) => setRecurringFormData({ ...recurringFormData, endDate: e.target.value })}
                    className="bg-slate-700 border-slate-600 h-8"
                    disabled={!recurringFormData.useEndDate}
                  />
                </label>
              </div>
            </div>

            {/* Theme */}
            <div className="space-y-2">
              <Label>{t('theme')}</Label>
              <Input
                value={recurringFormData.theme}
                onChange={(e) => setRecurringFormData({ ...recurringFormData, theme: e.target.value })}
                placeholder={t('theme')}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            {/* Default Assignments */}
            <div className="space-y-2">
              <Label>{t('assignments')}</Label>
              <p className="text-xs text-slate-400">{t('assign_member')}</p>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {roles.slice(0, 6).map(role => (
                  <div key={role.key} className="space-y-1">
                    <Label className="text-xs text-slate-400">{role.name}</Label>
                    <Input
                      value={recurringFormData.defaultAssignments[role.key]?.join(', ') || ''}
                      onChange={(e) => setRecurringFormData({
                        ...recurringFormData,
                        defaultAssignments: {
                          ...recurringFormData.defaultAssignments,
                          [role.key]: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                        }
                      })}
                      placeholder={t('select_member')}
                      className="bg-slate-800 border-slate-700 text-sm h-8"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecurringDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleCreateRecurringEvent} className="bg-purple-600 hover:bg-purple-700">
              <CalendarPlus className="w-4 h-4 mr-2" />
              {t('generate_schedules')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceCalendar;
